"use client";

/**
 * Avatar médecin avec fallback initiales en cas d'erreur de chargement image.
 *
 * Pattern : on rend toujours les initiales en background. L'<img> se superpose
 * en absolute SI elle charge avec succès (onLoad → opacity 100). Pendant le
 * fetch et en cas d'erreur, l'<img> reste opacity-0 et seules les initiales
 * sont visibles.
 *
 * A11y par défaut : alt="" + aria-hidden — l'accessibilité est gérée par le
 * span initiales + aria-label du Link parent. Pour Google Image Search (qui lit
 * `alt` et pas `aria-label`), passer `descriptiveAlt` opt-in : alt = ce texte,
 * aria-hidden retiré. Utilisé sur les listings publics (cf audit SEO 2026-05-17 P0-4).
 */

import { useEffect, useRef, useState } from "react";

type Props = {
  src?: string | null;
  firstName: string;
  lastName: string;
  /** @deprecated kept for API compat — use descriptiveAlt for SEO */
  alt?: string;
  /**
   * Si présent : alt={descriptiveAlt} et aria-hidden retiré. Sinon : alt=""
   * + aria-hidden (comportement par défaut, a11y via aria-label parent).
   * À passer sur les listings publics pour Google Image Search.
   */
  descriptiveAlt?: string;
  className?: string;
  initialsClassName?: string;
  /**
   * Above-the-fold (LCP candidate) — désactive lazy load et passe
   * fetchPriority="high". À utiliser uniquement sur l'avatar hero d'une
   * fiche médecin/centre. Cf SEO audit P2-11. Défaut : false (lazy).
   */
  priority?: boolean;
};

export function PhysicianAvatar({
  src,
  firstName,
  lastName,
  descriptiveAlt,
  className,
  initialsClassName,
  priority = false,
}: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const initials =
    `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase() || "?";
  const tryImage = !!src && !broken;
  const showImage = tryImage && loaded;

  // Garde-fou : si l'image était déjà en cache navigateur au mount (cas
  // navigation client-side après un premier passage), `onLoad` n'est pas
  // toujours déclenché. On vérifie .complete + .naturalWidth pour récupérer
  // le state. Évite l'image fantôme (opacity 0 jamais relevée) sur
  // l'avatar hero quand on revient sur la page.
  useEffect(() => {
    if (!tryImage) return;
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    }
    // Reset quand src change
    if (!el) return;
  }, [tryImage, src]);

  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <span
        className={
          initialsClassName ??
          "select-none font-bold text-[var(--sahha-primary-700)]"
        }
      >
        {initials}
      </span>
      {tryImage && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          src={src!}
          alt={descriptiveAlt ?? ""}
          {...(descriptiveAlt ? {} : { "aria-hidden": true })}
          className={`absolute inset-0 ${className ?? "h-full w-full object-cover"}`}
          style={{ opacity: showImage ? 1 : 0, transition: "opacity 200ms" }}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={() => setLoaded(true)}
          onError={() => setBroken(true)}
        />
      )}
    </div>
  );
}
